// Shows the error code of an unexpected server error.
//
// On an unhandled exception the API answers 500 with `{ ..., errorCode: "ERR-…" }`
// (ExceptionMiddleware). The same code is written to the system logs with the
// details and to the customer's activity record without them. Showing it here
// lets the customer quote it to support ("Hata kodu: ERR-7F3A2C1B"), and support
// finds the record in seconds.
//
// The component's own error toast ("Kaydedilemedi.") is more informative, so the
// code is APPENDED to it instead of showing a second toast — the customer used to
// see two stacked notifications. If no component toast arrives within a short
// window (some call sites swallow the error), the store shows the full sentence
// on its own. Only 5xx bodies that actually carry a code trigger it; 4xx business
// messages are left to the components, which already show them.

import { isPlatformBrowser } from '@angular/common';
import { HttpInterceptorFn } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, throwError } from 'rxjs';

import { NotificationStore } from '@/app/core/notifications/notification.store';
import { errorCodeOf } from './error-code';

export const errorCodeInterceptor: HttpInterceptorFn = (req, next) => {
  const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  const notify = inject(NotificationStore);
  // Optional: the public site does not load Transloco.
  const transloco = inject(TranslocoService, { optional: true });

  return next(req).pipe(
    catchError((error: unknown) => {
      const code = isBrowser ? errorCodeOf(error) : null;
      if (code) {
        const key = 'common.msg.unexpectedWithCode';
        const text = transloco?.translate(key, { code });
        const shortKey = 'common.msg.errorCodeShort';
        const short = transloco?.translate(shortKey, { code });
        notify.sistemHataKodu(
          short && short !== shortKey ? short : `Hata kodu: ${code}`,
          text && text !== key ? text : `Beklenmeyen bir hata oluştu. Hata kodu: ${code}`,
        );
      }
      return throwError(() => error);
    }),
  );
};
